import React from "react";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, Chip, Typography, Tooltip, Switch, IconButton, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Error from "../error/error";
import { useStyles } from "../../assets/styles/listTableStyle";
import EditIcon from "@mui/icons-material/Edit";
import { STATUS } from "../../utils/status";

export interface FeedbackTableEntry {
    id: string;
    stages: string[];
    feedback: string;
    longDescription?: string;
    vendor?: string | null;
    is_enabled: boolean;
    recipientCount: number;
    count?: number;

}

interface FeedbackTableProps {
    rows: FeedbackTableEntry[];
    handleToggleEnabled: (id: string) => void;
    handleDelete: (id: string) => void;
    handleEdit: (row: FeedbackTableEntry) => void;
    feedbacks?: {
        count?: number;
        results: FeedbackTableEntry[];
    };
    getFeedbackStatus?: string;
    getFeedbackMessage?: string;
}

enum FeedbackTableHeaders {
    Stage = "Stage",
    Feedback = "Feedback",
    Vendor = "Vendor Pseudo Name",
    AcknowledgedUsers = "Acknowledged Users",
    Actions = "Actions",
}




const FeedbackTable: React.FC<FeedbackTableProps> = ({ rows, handleToggleEnabled, handleDelete, handleEdit, feedbacks, getFeedbackStatus, getFeedbackMessage }) => {
    const classes = useStyles();

    return (
        <TableContainer sx={{ position: "relative" }}>
            <Table
                sx={{
                    width: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                }}
                aria-label="feedback table"
            >
                <TableHead sx={{ backgroundColor: "#F7F9FA", }}>
                    <TableRow>
                        {Object.values(FeedbackTableHeaders).map((header) => (
                            <TableCell
                                key={header}
                                align={header === "Actions" ? "center" : "left"}
                                sx={{ fontFamily: "montserrat", fontWeight: "bold", fontSize: "12px", }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>

                    {getFeedbackStatus === STATUS.PENDING ?
                        (<div style={{ display: 'flex', justifyContent: 'center', alignItems: "center", marginTop: '20px' }}>
                            <CircularProgress />
                        </div>)
                        :
                        (feedbacks?.results?.length ?? 0) > 0 ? (
                            feedbacks?.results?.map((row: any) => (
                                <TableRow key={row.id}>


                                    <TableCell sx={{ width: '220px', verticalAlign: 'top', paddingTop: '16px' }}>
                                        <Stack 
                                            direction="row" 
                                            spacing={0.5} 
                                            alignItems="flex-start"
                                            sx={{
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                rowGap: 0.5,
                                            }}
                                        >
                                            {row.assigned_stages?.map((stage: any) => (
                                                <Chip key={stage.id} label={stage.name} size="small" />
                                            ))}
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        <Tooltip title={row.title?.length > 30 ? row.title : ""} arrow>
                                            <Typography variant="subtitle2">
                                                {row.title?.length > 30 ? row.title.slice(0, 30) + "..." : row.title}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>


                                    <TableCell>
                                        {row.vendor_details?.pseudo_name ?? "-"}
                                    </TableCell>


                                    <TableCell>
                                        {row.read_count ?? "-"}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">

                                            <Tooltip title={row.is_enabled ? "Disable" : "Enable"}>
                                                <Switch
                                                    checked={row.is_enabled}
                                                    onChange={() => {
                                                        handleToggleEnabled(row)
                                                    }
                                                    }
                                                />
                                            </Tooltip>

                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => handleEdit(row)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>

                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} className={classes.noUsersMessage}>
                                    <Error message={'There are no feedbacks added as of yet. You can add a feedback to see it here.'} />
                                </TableCell>
                            </TableRow>
                        )}

                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FeedbackTable;
